import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LuBell } from 'react-icons/lu';

import { useNotifications } from '../../contexts/NotificationsContext';
import { useI18n } from '../../i18n';
import { NotificationCard } from './NotificationCard';

/**
 * Compact bell + dropdown for the dashboard's persistent header. Manager's own
 * notification feed. Hidden when the current route is /restaurant_admin/* (handled
 * by the parent ScorecardNavigation, not here).
 */
export const NotificationBell: React.FC = () => {
  const { items, unreadCount, refresh, markRead, markAllRead } = useNotifications();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const toggle = () => {
    if (!open) refresh();
    setOpen((o) => !o);
  };

  const handleItemClick = (id: string, deepLink: string | null) => {
    markRead(id);
    setOpen(false);
    if (deepLink && !deepLink.startsWith('bespoke://')) {
      navigate(deepLink);
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={toggle}
        aria-label={t('notifications.bell.aria')}
        style={{
          position: 'relative',
          width: 38,
          height: 38,
          borderRadius: '50%',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#374151',
        }}
      >
        <LuBell size={20} />
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              minWidth: 18,
              height: 18,
              borderRadius: 9,
              background: '#EF4444',
              color: 'white',
              fontSize: 10,
              fontWeight: 700,
              padding: '0 5px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid white',
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: 380,
            maxHeight: '70vh',
            overflowY: 'auto',
            background: 'white',
            border: '1px solid #E5E7EB',
            borderRadius: 12,
            boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              borderBottom: '1px solid #F3F4F6',
            }}
          >
            <div style={{ fontWeight: 700 }}>{t('notifications.title')}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={() => markAllRead()}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3B82F6', fontSize: 13 }}
                >
                  {t('notifications.markAllRead')}
                </button>
              )}
            </div>
          </div>
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {items.length === 0 && (
              <div style={{ textAlign: 'center', padding: 24, color: '#6B7280', fontSize: 13 }}>
                {t('notifications.empty.title')}
              </div>
            )}
            {items.slice(0, 5).map((n) => (
              <NotificationCard
                key={n.id}
                notification={n}
                onClick={() => handleItemClick(n.id, n.deep_link)}
              />
            ))}
            {items.length > 5 && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate('/restaurant/notifications');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#3B82F6',
                  fontSize: 13,
                  fontWeight: 600,
                  padding: 8,
                }}
              >
                {t('notifications.dropdown.viewAll')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
